// S18B-1 — Asset Register (OSWC GAP-7).
//
// Site-wise asset tree (camera→NVR→switch→UPS), filter/search, create/edit,
// and a PDF/Excel export. Uses the existing theme + ui components.
import { useMemo, useState } from "react";
import {
  ArrowDownIcon,
  ArrowUpIcon,
  ChevronDownIcon,
  ChevronRightIcon,
  DownloadIcon,
  PlusIcon,
} from "lucide-react";

import {
  exportAssets,
  useAssets,
  useAssetTree,
  useCreateAsset,
  useDeleteAsset,
  type Asset,
  type AssetCreateInput,
  type AssetTreeNode,
  type DeviceType,
} from "@/api/assets";
import { useSites } from "@/api/sites";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { ConfirmDialog } from "@/components/ui/confirm-dialog";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { QueryState } from "@/components/QueryState";
import { useCameras } from "@/api/cameras";

const NAVY = "#263238";
const ACCENT = "#ff6d00";

const DEVICE_TYPES: DeviceType[] = [
  "camera",
  "nvr",
  "switch",
  "ups",
  "router",
  "other",
];

function downloadBlob(blob: Blob, name: string) {
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = name;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  setTimeout(() => URL.revokeObjectURL(url), 1000);
}


// ---------------------------------------------------------------------------
// T4a — the register renders STRUCTURED rows, because /assets already returns
// them and the tree was throwing them away.
//
// Measured on prod 2026-08-06: 120 assets on one site — 114 `camera`, 6 `nvr`.
//   asset_tag  0 / 120     <- the tree's FIRST choice of label. Never set.
//   vendor    92 / 120
//   model     18 / 120
//   serial_no 114 / 114 cameras   <- present on every one, shown nowhere
//   linked_camera_id  13          <- 107 assets link to no camera at all
//
// So the tree labelled each row `asset_tag || model || vendor || id.slice(0,8)`
// and, with no tags, 22 rows fell through to a bare UUID prefix while the
// serial that identifies the unit sat unrendered. That is the "flat chips with
// UUIDs" finding: not missing data, DISCARDED data.
// ---------------------------------------------------------------------------

/** Never a bare UUID. Prefers the operator-facing tag, then vendor+model, then
 *  the serial — which is the one field this fleet actually populates. */
export function assetLabel(a: {
  asset_tag?: string | null; vendor?: string | null;
  model?: string | null; serial_no?: string | null; id: string;
}): string {
  const tag = (a.asset_tag ?? "").trim();
  if (tag) return tag;
  const vm = [a.vendor, a.model].map((x) => (x ?? "").trim()).filter(Boolean).join(" ");
  if (vm) return vm;
  const serial = (a.serial_no ?? "").trim();
  if (serial) return serial;
  return `Unidentified asset ${a.id.slice(0, 8)}`;
}

type LinkState = "live" | "retired" | "unlinked";

/** `assets` has NO status column (verified against information_schema), so an
 *  asset is not "active" or "inactive" in its own right. The only status it can
 *  carry is that of the camera it points at — and 107 of 120 point at nothing.
 *  Inventing an asset status would be inventing a fact. */
export function linkState(
  a: { linked_camera_id?: string | null },
  liveCameraIds: Set<string>,
  knownCameraIds: Set<string>,
): LinkState {
  const id = a.linked_camera_id ?? "";
  if (!id) return "unlinked";
  if (liveCameraIds.has(id)) return "live";
  if (knownCameraIds.has(id)) return "retired";
  return "retired";
}

type SortKey = "label" | "device_type" | "vendor" | "model" | "serial_no" | "link" | "warranty_expiry";

const PAGE_SIZE = 25;

export function AssetRegisterPage() {
  const sites = useSites();
  const [siteId, setSiteId] = useState<string>("");
  const [deviceType, setDeviceType] = useState<string>("");
  const [vendor, setVendor] = useState<string>("");
  const [assetTag, setAssetTag] = useState<string>("");
  const [addOpen, setAddOpen] = useState(false);
  const [exporting, setExporting] = useState(false);
  // DECISION (T4a-i): the TABLE is the default view and the tree is a toggle.
  // The tree is legitimate — parent_asset_id is a real hierarchy — but it was
  // the default while discarding every structured column, which is the finding.
  // Deleting it would lose information, so it stays one click away.
  const [view, setView] = useState<"table" | "tree">("table");
  // DECISION (T4a-i): assets have NO status column, so "show inactive?" is not
  // a question this table can answer about the asset. What it CAN answer is
  // whether the asset points at a live camera. Default "all" — hiding rows an
  // operator may need to act on is worse than showing a composition they can
  // read. The header states that composition so the count means something.
  const [linkFilter, setLinkFilter] = useState<"all" | "live" | "retired" | "unlinked">("all");

  // Default to the first site once loaded.
  const effectiveSite =
    siteId || sites.data?.items?.[0]?.id || "";

  const tree = useAssetTree(view === "tree" ? effectiveSite : undefined);
  // The table always fetches with the current filters — it does not depend on
  // whether any are set, which is what made the structured view unreachable
  // until you happened to type in a filter box.
  const flat = useAssets({
    site_id: effectiveSite || undefined,
    device_type: deviceType || undefined,
    vendor: vendor || undefined,
    asset_tag: assetTag || undefined,
  });

  // Camera link resolution. `cameras` returns the LIVE inventory, so an id that
  // is absent from it is a retired camera rather than an unknown one.
  const cams = useCameras({ limit: 500 });
  const liveCameraIds = useMemo(
    () => new Set((cams.data?.items ?? []).map((c) => c.id)),
    [cams.data],
  );
  const knownCameraIds = liveCameraIds;
  const siteNames = useMemo(() => {
    const m: Record<string, string> = {};
    for (const st of sites.data?.items ?? []) m[st.id] = st.name;
    return m;
  }, [sites.data]);

  const allAssets = flat.data?.items ?? [];
  const counts = useMemo(() => {
    let live = 0, retired = 0, unlinked = 0;
    for (const a of allAssets) {
      const l = linkState(a, liveCameraIds, knownCameraIds);
      if (l === "live") live += 1;
      else if (l === "retired") retired += 1;
      else unlinked += 1;
    }
    return { total: allAssets.length, live, retired, unlinked };
  }, [allAssets, liveCameraIds, knownCameraIds]);

  const visibleAssets = useMemo(
    () =>
      linkFilter === "all"
        ? allAssets
        : allAssets.filter(
            (a) => linkState(a, liveCameraIds, knownCameraIds) === linkFilter,
          ),
    [allAssets, linkFilter, liveCameraIds, knownCameraIds],
  );

  async function onExport(format: "pdf" | "excel") {
    if (!effectiveSite) return;
    setExporting(true);
    try {
      const blob = await exportAssets(effectiveSite, format);
      downloadBlob(blob, `asset-register.${format === "excel" ? "xlsx" : "pdf"}`);
    } finally {
      setExporting(false);
    }
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-xl font-semibold" style={{ color: NAVY }}>
            Asset Register
          </h1>
          {/* T4a-i — a bare "120 assets" is not actionable next to a fleet of
              15 live cameras. The composition is: what is linked to something
              live, what points at a retired camera, and what is inventory that
              was never linked at all. All three are facts an operator can act
              on; the single total was not. */}
          <p className="text-sm text-muted-foreground">
            Site equipment inventory — camera, NVR, switch, UPS, router.
          </p>
          {!flat.isLoading && !flat.isError && (
            <p className="mt-0.5 text-xs text-muted-foreground">
              <strong>{counts.total}</strong> assets ·{" "}
              <span className="text-emerald-700">{counts.live} linked to a live camera</span> ·{" "}
              {counts.retired} linked to a retired camera ·{" "}
              {counts.unlinked} not linked
            </p>
          )}
        </div>
        <div className="flex gap-2">
          <Button
            variant="outline"
            disabled={!effectiveSite || exporting}
            onClick={() => onExport("pdf")}
          >
            <DownloadIcon className="mr-1 h-4 w-4" /> PDF
          </Button>
          <Button
            variant="outline"
            disabled={!effectiveSite || exporting}
            onClick={() => onExport("excel")}
          >
            <DownloadIcon className="mr-1 h-4 w-4" /> Excel
          </Button>
          <Button
            style={{ backgroundColor: ACCENT }}
            disabled={!effectiveSite}
            onClick={() => setAddOpen(true)}
          >
            <PlusIcon className="mr-1 h-4 w-4" /> Add asset
          </Button>
        </div>
      </div>

      {/* Filter bar */}
      <Card>
        <CardContent className="flex flex-wrap items-end gap-3 p-4">
          <div className="space-y-1">
            <Label>Site</Label>
            <select
              className="h-9 rounded-md border border-input bg-background px-2 text-sm"
              value={effectiveSite}
              onChange={(e) => setSiteId(e.target.value)}
            >
              {(sites.data?.items ?? []).map((s) => (
                <option key={s.id} value={s.id}>
                  {s.name}
                </option>
              ))}
            </select>
          </div>
          <div className="space-y-1">
            <Label>Device type</Label>
            <select
              className="h-9 rounded-md border border-input bg-background px-2 text-sm"
              value={deviceType}
              onChange={(e) => setDeviceType(e.target.value)}
            >
              <option value="">All</option>
              {DEVICE_TYPES.map((d) => (
                <option key={d} value={d}>
                  {d}
                </option>
              ))}
            </select>
          </div>
          <div className="space-y-1">
            <Label>Vendor</Label>
            <Input
              value={vendor}
              onChange={(e) => setVendor(e.target.value)}
              placeholder="e.g. Hikvision"
            />
          </div>
          <div className="space-y-1">
            <Label>Asset tag (QR)</Label>
            <Input
              value={assetTag}
              onChange={(e) => setAssetTag(e.target.value)}
              placeholder="scan / type tag"
            />
          </div>
          <div className="space-y-1">
            <Label>Camera link</Label>
            <select
              className="h-9 rounded-md border border-input bg-background px-2 text-sm"
              value={linkFilter}
              onChange={(e) =>
                setLinkFilter(e.target.value as typeof linkFilter)
              }
            >
              <option value="all">All</option>
              <option value="live">Linked to a live camera</option>
              <option value="retired">Linked to a retired camera</option>
              <option value="unlinked">Not linked</option>
            </select>
          </div>
          <div className="space-y-1">
            <Label>View</Label>
            <div className="flex h-9 items-center gap-1">
              <Button
                variant={view === "table" ? "default" : "outline"}
                size="sm"
                onClick={() => setView("table")}
              >
                Table
              </Button>
              <Button
                variant={view === "tree" ? "default" : "outline"}
                size="sm"
                onClick={() => setView("tree")}
              >
                Tree
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* T4a — the TABLE is the default. It used to render only when a filter
          happened to be set, so the structured columns the API returns were
          unreachable on a normal page load. */}
      {view === "table" ? (
        <AssetTable
          assets={visibleAssets}
          loading={flat.isLoading}
          isError={flat.isError}
          error={flat.error}
          onRetry={flat.refetch}
          siteNames={siteNames}
          liveCameraIds={liveCameraIds}
          knownCameraIds={knownCameraIds}
        />
      ) : (
        <Card>
          <CardContent className="p-4">
            {tree.isLoading ? (
              <p className="text-sm text-muted-foreground">Loading…</p>
            ) : (tree.data?.nodes ?? []).length === 0 ? (
              <p className="text-sm text-muted-foreground">
                No assets for this site yet. Use “Add asset”.
              </p>
            ) : (
              <ul className="space-y-1">
                {(tree.data?.nodes ?? []).map((n) => (
                  <TreeRow key={n.id} node={n} depth={0} />
                ))}
              </ul>
            )}
          </CardContent>
        </Card>
      )}

      {addOpen && (
        <AddAssetDialog
          siteId={effectiveSite}
          onClose={() => setAddOpen(false)}
        />
      )}
    </div>
  );
}

function TreeRow({ node, depth }: { node: AssetTreeNode; depth: number }) {
  const [open, setOpen] = useState(true);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const del = useDeleteAsset();
  const hasChildren = node.children.length > 0;
  const label = node.asset_tag || node.model || node.vendor || node.id.slice(0, 8);
  return (
    <li>
      <div
        className="flex items-center gap-2 rounded px-1 py-1 hover:bg-muted/50"
        style={{ paddingLeft: depth * 18 }}
      >
        {hasChildren ? (
          <button onClick={() => setOpen((o) => !o)} className="text-muted-foreground">
            {open ? (
              <ChevronDownIcon className="h-4 w-4" />
            ) : (
              <ChevronRightIcon className="h-4 w-4" />
            )}
          </button>
        ) : (
          <span className="inline-block w-4" />
        )}
        <span
          className="rounded px-1.5 py-0.5 text-xs font-medium text-white"
          style={{ backgroundColor: NAVY }}
        >
          {node.device_type}
        </span>
        <span className="text-sm">{label}</span>
        {node.vendor && (
          <span className="text-xs text-muted-foreground">· {node.vendor}</span>
        )}
        <button
          className="ml-auto text-xs text-red-600 hover:underline"
          onClick={() => setConfirmOpen(true)}
        >
          delete
        </button>
      </div>
      <ConfirmDialog
        open={confirmOpen}
        onOpenChange={setConfirmOpen}
        title="Delete asset?"
        description={
          <>
            This removes <span className="font-semibold">{label}</span> from the Asset
            Register. This cannot be undone.
          </>
        }
        confirmLabel="Delete asset"
        busy={del.isPending}
        onConfirm={() =>
          del.mutate(node.id, { onSuccess: () => setConfirmOpen(false) })
        }
      />
      {open &&
        node.children.map((c) => <TreeRow key={c.id} node={c} depth={depth + 1} />)}
    </li>
  );
}

interface AssetTableProps {
  assets: Asset[];
  loading: boolean;
  isError: boolean;
  error: unknown;
  onRetry: () => void;
  siteNames: Record<string, string>;
  liveCameraIds: Set<string>;
  knownCameraIds: Set<string>;
}

/** The register proper: sortable, paginated, and every column a fact the API
 *  already returned. */
function AssetTable({
  assets, loading, isError, error, onRetry,
  siteNames, liveCameraIds, knownCameraIds,
}: AssetTableProps) {
  const [sortKey, setSortKey] = useState<SortKey>("label");
  const [asc, setAsc] = useState(true);
  const [page, setPage] = useState(0);

  const rows = useMemo(() => {
    const decorated = assets.map((a) => ({
      a,
      label: assetLabel(a),
      link: linkState(a, liveCameraIds, knownCameraIds),
      site: siteNames[a.site_id] ?? a.site_id.slice(0, 8),
    }));
    const val = (r: (typeof decorated)[number]): string => {
      switch (sortKey) {
        case "label": return r.label;
        case "device_type": return r.a.device_type;
        case "vendor": return r.a.vendor ?? "";
        case "model": return r.a.model ?? "";
        case "serial_no": return r.a.serial_no ?? "";
        case "link": return r.link;
        case "warranty_expiry": return r.a.warranty_expiry ?? "";
      }
    };
    return decorated.sort((x, y) => {
      const c = val(x).localeCompare(val(y), undefined, { numeric: true });
      return asc ? c : -c;
    });
  }, [assets, sortKey, asc, siteNames, liveCameraIds, knownCameraIds]);

  const pageCount = Math.max(1, Math.ceil(rows.length / PAGE_SIZE));
  const clamped = Math.min(page, pageCount - 1);
  const slice = rows.slice(clamped * PAGE_SIZE, clamped * PAGE_SIZE + PAGE_SIZE);

  function Th({ k, children }: { k: SortKey; children: React.ReactNode }) {
    const active = sortKey === k;
    return (
      <th className="px-4 py-2">
        <button
          type="button"
          className="inline-flex items-center gap-1 hover:text-foreground"
          onClick={() => {
            if (active) setAsc((v) => !v);
            else { setSortKey(k); setAsc(true); }
          }}
        >
          {children}
          {active && (asc
            ? <ArrowUpIcon className="h-3 w-3" />
            : <ArrowDownIcon className="h-3 w-3" />)}
        </button>
      </th>
    );
  }

  const linkBadge = (l: LinkState) => {
    if (l === "live") {
      return <span className="rounded bg-emerald-100 px-1.5 py-0.5 text-[11px] text-emerald-800">live camera</span>;
    }
    if (l === "retired") {
      return <span className="rounded bg-amber-100 px-1.5 py-0.5 text-[11px] text-amber-800">retired camera</span>;
    }
    // NOT an error state. 107 of 120 assets are inventory rows that were never
    // linked to a camera, which is a normal thing for an asset register to hold.
    return <span className="text-[11px] text-muted-foreground">not linked</span>;
  };

  return (
    <QueryState
      isLoading={loading}
      isError={isError}
      error={error}
      label="the asset register"
      onRetry={onRetry}
      isEmpty={assets.length === 0}
      emptyMessage="No assets match these filters."
    >
      <Card>
        <CardContent className="p-0">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b text-left text-xs uppercase text-muted-foreground">
                <Th k="label">Asset</Th>
                <Th k="device_type">Type</Th>
                <th className="px-4 py-2">Site</th>
                <Th k="vendor">Vendor</Th>
                <Th k="model">Model</Th>
                <Th k="serial_no">Serial</Th>
                <Th k="link">Link</Th>
                <Th k="warranty_expiry">Warranty</Th>
              </tr>
            </thead>
            <tbody>
              {slice.map(({ a, label, link, site }) => (
                <tr key={a.id} className="border-b hover:bg-muted/20">
                  <td className="px-4 py-2 font-medium">{label}</td>
                  <td className="px-4 py-2 text-xs">{a.device_type}</td>
                  <td className="px-4 py-2 text-xs text-muted-foreground">{site}</td>
                  <td className="px-4 py-2 text-xs">{a.vendor || "—"}</td>
                  <td className="px-4 py-2 text-xs">{a.model || "—"}</td>
                  <td className="px-4 py-2 font-mono text-[11px]">{a.serial_no || "—"}</td>
                  <td className="px-4 py-2">{linkBadge(link)}</td>
                  <td className="px-4 py-2 text-xs">{a.warranty_expiry || "—"}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </CardContent>
      </Card>

      {rows.length > PAGE_SIZE && (
        <div className="mt-3 flex items-center justify-between text-sm">
          <span className="text-muted-foreground">
            Showing {clamped * PAGE_SIZE + 1}–
            {Math.min((clamped + 1) * PAGE_SIZE, rows.length)} of {rows.length}
          </span>
          <div className="flex gap-2">
            <Button variant="outline" size="sm" disabled={clamped === 0}
                    onClick={() => setPage(clamped - 1)}>Previous</Button>
            <span className="self-center text-xs text-muted-foreground">
              {clamped + 1} / {pageCount}
            </span>
            <Button variant="outline" size="sm" disabled={clamped >= pageCount - 1}
                    onClick={() => setPage(clamped + 1)}>Next</Button>
          </div>
        </div>
      )}
    </QueryState>
  );
}

function AddAssetDialog({
  siteId,
  onClose,
}: {
  siteId: string;
  onClose: () => void;
}) {
  const create = useCreateAsset();
  const [form, setForm] = useState<AssetCreateInput>({
    site_id: siteId,
    device_type: "camera",
  });
  const [err, setErr] = useState<string | null>(null);

  const set = (k: keyof AssetCreateInput, v: string) =>
    setForm((f) => ({ ...f, [k]: v || undefined }));

  async function submit() {
    setErr(null);
    try {
      await create.mutateAsync({ ...form, site_id: siteId });
      onClose();
    } catch (e: unknown) {
      const detail =
        (e as { response?: { data?: { detail?: string } } })?.response?.data
          ?.detail ?? "Failed to create asset";
      setErr(typeof detail === "string" ? detail : "Failed to create asset");
    }
  }

  const fields = useMemo(
    () =>
      [
        ["asset_tag", "Asset tag (QR/barcode)"],
        ["vendor", "Vendor"],
        ["model", "Model"],
        ["serial_no", "Serial no"],
        ["install_date", "Install date (YYYY-MM-DD)"],
        ["warranty_expiry", "Warranty expiry (YYYY-MM-DD)"],
        ["linked_camera_id", "Linked camera id (optional)"],
        ["parent_asset_id", "Parent asset id (optional)"],
      ] as [keyof AssetCreateInput, string][],
    [],
  );

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Add asset</DialogTitle>
        </DialogHeader>
        <div className="grid grid-cols-2 gap-3">
          <div className="space-y-1">
            <Label>Device type</Label>
            <select
              className="h-9 w-full rounded-md border border-input bg-background px-2 text-sm"
              value={form.device_type}
              onChange={(e) =>
                setForm((f) => ({ ...f, device_type: e.target.value as DeviceType }))
              }
            >
              {DEVICE_TYPES.map((d) => (
                <option key={d} value={d}>
                  {d}
                </option>
              ))}
            </select>
          </div>
          {fields.map(([k, label]) => (
            <div key={k} className="space-y-1">
              <Label>{label}</Label>
              <Input
                value={(form[k] as string) ?? ""}
                onChange={(e) => set(k, e.target.value)}
              />
            </div>
          ))}
        </div>
        {err && <p className="text-sm text-red-600">{err}</p>}
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>
            Cancel
          </Button>
          <Button
            style={{ backgroundColor: ACCENT }}
            disabled={create.isPending}
            onClick={submit}
          >
            Create
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default AssetRegisterPage;
